import React, { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useStickyHeader } from '~/shared/utils/StickyHeaderContext';

import Breadcrumbs from '../components/Breadcrumbs';
import CategoryTabs from '../components/CategoryTabs';
import DiscoveryGuide from '../components/DiscoveryGuide';
import FiltersPanel from '../components/Filters/FiltersPanel';
import FloatingControls from '../components/FloatingControls';
import GridScrollHeader from '../components/GridScrollHeader';
import GridSpotlights from '../components/GridSpotlights';
import NumResults from '../components/NumResults';
import VirtualScrollGrid from '../components/VirtualScrollGrid';
import {
  useGridFacets,
  useGridMeta,
  useGridProducts,
} from '../context/GridDataContexts';
import GridLayoutTrackingProvider from '../context/GridLayoutTrackingProvider';
import { useGridTabsPortal } from '../context/GridTabsPortalContext';
import { PendingFiltersProvider } from '../context/PendingFiltersContext';
import { useAutoFilters } from '../hooks/useAutofilters';
import useGridInputs from '../hooks/useGridInputs';
import useInGridAssets from '../hooks/useInGridAssets';
import useTabsAndBreadcrumbs from '../hooks/useTabsAndBreadcrumbs';
import { getGridLayoutTrackingTags } from '../utils/tracking';

const BreadcrumbsAndNumResultsContainer = styled.div<{ $hasTabs: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0px 16px;
  padding: 12px 0px ${({ $hasTabs }) => ($hasTabs ? '8px' : '12px')};
`;

const Flyout = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: white;
  z-index: 10000;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease;

  /* Hide scrollbar */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

const GridViewContainer = styled.div`
  overflow-x: hidden;
  overscroll-behavior-x: none;
  margin-top: 2px;
`;

const MobileGridView = () => {
  const { setAutoHide } = useStickyHeader() as {
    setAutoHide: React.Dispatch<React.SetStateAction<boolean>>;
  };
  const breadcrumbsRef = useRef<HTMLDivElement>(null);
  const numResultsRef = useRef<HTMLDivElement>(null);
  const { tabs } = useTabsAndBreadcrumbs();
  const hasTabs = Boolean(tabs?.length);
  const tabsPortalTarget = useGridTabsPortal();
  const [isFiltersPaneOpen, setIsFiltersPaneOpen] = useState(false);
  const openFiltersPanel = () => setIsFiltersPaneOpen(true);
  const closeFiltersPanel = () => setIsFiltersPaneOpen(false);

  // Enable StickyHeader auto-hide while this view is mounted
  useEffect(() => {
    setAutoHide(true);
    return () => setAutoHide(false);
  }, [setAutoHide]);

  useEffect(() => {
    if (isFiltersPaneOpen) {
      // Compensate for scrollbar width to prevent layout shift.
      // A plain `overflow: hidden` removes the scrollbar, which changes
      // the viewport width and fires a scroll event that the Sticky
      // header's autoHide interprets as a scroll-down, hiding the header.
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isFiltersPaneOpen]);

  const {
    products,
    fetchNextPage,
    hasNextPage,
    isPrimaryProductsFetching,
    isPrimaryProductsLoading,
    isRelatedProductsFetching,
    isProductPricingFetching,
  } = useGridProducts();

  const { facets, totalNumResults } = useGridFacets();

  const { cioFilterName, cioFilterValue } = useGridMeta();

  const { mergedAssetsAndProducts } = useInGridAssets({ products });
  const { builderState, gridGender, gridType } = useGridInputs();

  const { autofilterProcessed } = useAutoFilters({
    builderState,
    gridGender,
    facets,
    isPrimaryProductsLoading,
  });

  const showFloatingControls = autofilterProcessed && facets?.length > 0;

  const { trackingTags } = getGridLayoutTrackingTags({
    totalNumResults,
    cioFilterName,
    cioFilterValue,
    gridType,
  });

  // Breadcrumbs (with NumResults — they share a row), and Tabs are portaled
  // to a slot at the top of the page body so they always render above any
  // Builder content (banner / Vimeo / etc.) that an author drops above the
  // grid block. Refs keep working — `createPortal` preserves the React tree.
  //
  // We render `null` (not an inline fallback) until the portal target attaches
  // so the React element type in this slot is stable — switching from inline
  // JSX to a portal element across renders would unmount/remount Breadcrumbs +
  // CategoryTabs and trigger a one-frame layout jump from the inline position
  // to the portaled one. The provider attaches its target synchronously inside
  // React's commit phase, so the empty render lasts < 1 paint.
  const breadcrumbsAndTabs: any = tabsPortalTarget
    ? createPortal(
        <>
          <BreadcrumbsAndNumResultsContainer
            ref={breadcrumbsRef}
            $hasTabs={hasTabs}
          >
            <Breadcrumbs />
            <div ref={numResultsRef}>
              <NumResults
                totalNumResults={totalNumResults}
                isLoading={isPrimaryProductsLoading}
              />
            </div>
          </BreadcrumbsAndNumResultsContainer>
          <CategoryTabs />
        </>,
        tabsPortalTarget
      )
    : null;

  return (
    <>
      <GridLayoutTrackingProvider>
        <GridViewContainer>
          <GridScrollHeader
            variant="mobile"
            breadcrumbsRef={breadcrumbsRef}
            numResultsRef={numResultsRef}
            totalNumResults={totalNumResults}
          />
          {breadcrumbsAndTabs}
          <div {...trackingTags}>
            <GridSpotlights />
            <DiscoveryGuide />
            <VirtualScrollGrid
              virtualList={mergedAssetsAndProducts}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isPrimaryProductsLoading={isPrimaryProductsLoading}
              isPrimaryProductsFetching={isPrimaryProductsFetching}
              isRelatedProductsFetching={isRelatedProductsFetching}
              isProductPricingFetching={isProductPricingFetching}
              setIsFiltersPaneOpen={setIsFiltersPaneOpen}
            />
            {showFloatingControls && (
              <FloatingControls openFiltersPanel={openFiltersPanel} />
            )}
          </div>
        </GridViewContainer>
      </GridLayoutTrackingProvider>
      {createPortal(
        <Flyout $isOpen={isFiltersPaneOpen}>
          <PendingFiltersProvider>
            <FiltersPanel
              closeFiltersPanel={closeFiltersPanel}
              showSortDropdown
            />
          </PendingFiltersProvider>
        </Flyout>,
        document.body
      )}
    </>
  );
};

export default MobileGridView;
